import { reactive } from "vue";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";
import { Preferences } from "@capacitor/preferences";
import { Device } from "@capacitor/device";
import { App } from "@capacitor/app";
import { setNotificationSignOut } from "./notificationDeviceSession";

const nativeNotifications = registerPlugin("ForktoNotifications");
export const notificationDeviceState = reactive({
    installationId: "",
    permission: "unknown",
    busy: false,
    error: "",
    receivedTest: ""
});
let activeController;
export const isNativeNotifications = () => Capacitor.isNativePlatform() && !import.meta.env.DEV;

// The installation ID survives account changes. Revision ordering prevents an
// old registration/logout request from reassigning a newly shared installation.
export function createNotificationDeviceController({
    plugin,
    preferences,
    device,
    app,
    api,
    identity,
    state,
    deactivateNative,
    navigate,
    invalidate
}) {
    let installation = "";
    let revision = 0;
    let generation = 0;
    let token = "";
    let handles = [];
    let pending = null;
    let stopped = false;
    let serial = Promise.resolve();
    let signedOut = null;
    let deactivation = Promise.resolve();
    let pendingTap = null;
    const key = (actor) => (actor?.user && actor?.tenant ? `${actor.tenant}:${actor.user}` : "");
    const headers = (actor) => ({
        Authorization: `Bearer ${actor.token}`,
        "Customer-ID": actor.tenant
    });
    const nextRevision = async () => {
        const next = ++revision;
        await preferences.set({ key: "forkto.push.revision", value: String(next) });
        return next;
    };
    const enqueue = (work) => {
        const result = serial.then(work, work);
        serial = result.catch(() => {});
        return result;
    };
    const same = (actor, epoch) =>
        !stopped && generation === epoch && key(identity()) === key(actor);
    async function save(actor, epoch, restore = false) {
        if (!same(actor, epoch) || !token) return;
        const details = await device.getInfo();
        const info = await app.getInfo();
        if (!same(actor, epoch)) return;
        const next = await nextRevision();
        const result = await api.RegisterNotificationDevice(
            {
                installation_id: installation,
                revision: next,
                token,
                platform: details.platform,
                model: details.model,
                app_version: info.version,
                permission: "granted",
                restore
            },
            { headers: headers(actor) }
        );
        if (same(actor, epoch)) {
            state.error = "";
            signedOut = null;
            await invalidate();
        }
        return result;
    }
    async function receipt(notification, tapped = false) {
        const data = notification?.data;
        const actor = identity();
        if (tapped && data?.type === "device_test" && !key(actor)) {
            pendingTap = notification;
            return;
        }
        if (
            data?.type !== "device_test" ||
            data.user_id !== actor?.user ||
            data.tenant !== actor?.tenant ||
            !Number.isFinite(Number(data.expires_at)) ||
            Number(data.expires_at) <= Date.now()
        )
            return;
        state.receivedTest = data.test_id;
        if (tapped) await navigate({ path: "/profile", query: { section: "notifications" } });
    }
    return {
        async start() {
            installation =
                (await preferences.get({ key: "forkto.push.installation" })).value ||
                crypto.randomUUID();
            await preferences.set({ key: "forkto.push.installation", value: installation });
            revision = Number((await preferences.get({ key: "forkto.push.revision" })).value) || 0;
            state.installationId = installation;
            handles.push(
                await plugin.addListener("registration", ({ value }) => {
                    token = value;
                    if (pending) {
                        pending.resolve();
                        return;
                    }
                    const actor = identity();
                    const epoch = generation;
                    if (key(actor))
                        void enqueue(() => save(actor, epoch)).catch(() => {
                            state.error = "Could not update this device. Try again.";
                        });
                })
            );
            handles.push(
                await plugin.addListener("registrationError", () => {
                    pending?.reject(new Error("The device could not register for notifications."));
                })
            );
            handles.push(
                await plugin.addListener("pushNotificationReceived", (notification) => {
                    void receipt(notification);
                })
            );
            handles.push(
                await plugin.addListener("pushNotificationActionPerformed", (action) => {
                    void receipt(action.notification, true);
                })
            );
        },
        sync({ requestPermission = false, restore = false } = {}) {
            const actor = identity();
            const epoch = generation;
            return enqueue(async () => {
                if (!same(actor, epoch) || !key(actor)) return;
                await deactivation;
                if (pendingTap) { const tapped = pendingTap; pendingTap = null; await receipt(tapped, true); }
                state.busy = true;
                state.error = "";
                try {
                    let permission = await plugin.checkPermissions();
                    if (permission.receive === "prompt-with-rationale")
                        permission = { receive: "prompt" };
                    if (permission.receive === "prompt" && requestPermission)
                        permission = await plugin.requestPermissions();
                    if (!same(actor, epoch)) return;
                    state.permission = permission.receive;
                    if (permission.receive !== "granted") {
                        await api.ReportNotificationPermission(
                            { installation_id: installation, permission: permission.receive },
                            { headers: headers(actor) }
                        );
                        return;
                    }
                    // Registration emits the current FCM token asynchronously.
                    let timer;
                    const registered = new Promise((resolve, reject) => {
                        pending = { resolve, reject };
                        timer = setTimeout(
                            () =>
                                reject(
                                    new Error("Notification registration timed out. Try again.")
                                ),
                            12000
                        );
                    });
                    try {
                        await plugin.register();
                        await registered;
                    } finally {
                        clearTimeout(timer);
                        pending = null;
                    }
                    const result = await save(actor, epoch, restore);
                    return result;
                } catch (error) {
                    if (same(actor, epoch))
                        state.error =
                            "Could not register this device. Check your connection and try again.";
                    throw error;
                } finally {
                    state.busy = false;
                }
            });
        },
        async signOut() {
            const actor = identity();
            if (!key(actor) || (signedOut?.key === key(actor) && signedOut?.token === actor.token))
                return signedOut?.meta || {};
            generation++;
            state.receivedTest = "";
            token = "";
            const next = await nextRevision();
            const meta = { installation_id: installation, notification_revision: next };
            signedOut = { key: key(actor), token: actor.token, meta };
            // Do not delay local sign-out on connectivity. Backend ownership
            // changes are also ordered; native unregistration runs immediately.
            void api
                .DetachNotificationDevice(
                    { installation_id: installation, revision: next },
                    { headers: headers(actor), skipSentry: true }
                )
                .catch(() => {});
            deactivation = Promise.allSettled([
                deactivateNative(),
                plugin.removeAllDeliveredNotifications?.()
            ]);
            return meta;
        },
        async stop() {
            stopped = true;
            generation++;
            await Promise.all(handles.map((handle) => handle.remove()));
            handles = [];
        }
    };
}

export async function startNotificationDevices(options) {
    if (!isNativeNotifications()) return;
    if (activeController) await activeController.stop();
    activeController = createNotificationDeviceController({
        ...options,
        plugin: PushNotifications,
        preferences: Preferences,
        device: Device,
        app: App,
        state: notificationDeviceState,
        deactivateNative: () =>
            Capacitor.getPlatform() === "ios"
                ? nativeNotifications.deactivate()
                : PushNotifications.unregister()
    });
    try {
        await activeController.start();
        setNotificationSignOut(() => activeController.signOut());
    } catch (error) {
        await activeController.stop();
        throw error;
    }
}
export const syncNotificationDevice = (options) =>
    activeController?.sync(options) || Promise.resolve();
export const stopNotificationDevices = () => {
    setNotificationSignOut(null);
    return activeController?.stop();
};
export async function openNotificationSettings() {
    if (Capacitor.getPlatform() === "ios") await nativeNotifications.openSettings();
}
